import { useTranslation } from 'react-i18next';
import type { IconName } from '@hotjar/hotsauce';
import { Text, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { CopyToClipboard } from '@hotjar/webapp/components/CopyToClipboard';
import { Icon } from '@hotjar/webapp/components/Icon';
import { ForwardPanel } from '@hotjar/webapp/features/Forwarding/ForwardSurveyPanel';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import SPINNER_ICON from '@hotjar/shared-assets/images/icons/icon-status-running.gif';
import { useShortUrl } from '@hotjar/webapp/features/ShortLink/use-short-url';
import {
  ZapierTemplate,
  prefilledTemplates as zapierTemplates,
} from '@hotjar/webapp/features/Forwarding/ForwardToZapier';
import { WebhooksFeature } from '@hotjar/integrations';
import { useCurrentSite } from '@hotjar/site';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: ${tokens.spacer.x2} ${tokens.spacer.x4} ${tokens.spacer.x4};
`;

const ShareBox = styled.div`
  padding: 0 ${tokens.spacer.x2} ${tokens.spacer.x3};
  width: 480px;
  background: ${tokens.color.neutral_100};
  border-radius: 8px;

  p {
    margin: 0 0 20px;
  }
`;
const LinkBox = styled.div`
  background: ${tokens.color.ui_background};
  border-radius: 5px;
`;
const CopyButton = styled(CopyToClipboard)`
  align-self: flex-start;
  flex: auto 0 0;
  margin-left: 10px;
  cursor: pointer;
`;

const ShareUrlContainer = styled.div`
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  display: flex;
  justify-content: space-between;
`;

const ShareUrl = styled.span`
  word-break: break-word;
`;

const ForwardResponsesTitle = styled(Text)`
  display: inline-block;
  padding-top: ${tokens.spacer.x3};
  padding-bottom: ${tokens.spacer.x1};
`;

export interface NewSurveyMessageProps {
  siteId: SiteId;
  surveyId: number;
  emailEnabled: boolean;
  onEdit: () => void;
}

export function NewSurveyMessage({
  siteId,
  surveyId,
  emailEnabled,
  onEdit,
}: NewSurveyMessageProps): JSX.Element {
  const { itemShared: trackItemShared } = useTrack();
  const { t } = useTranslation();
  const { data: site } = useCurrentSite();

  const webhooksEnabled = WebhooksFeature.canUse(site);

  const { click: trackClick } = useTrack();

  const trackZapier = (name: string) => {
    const uiPosition = name === 'Zapier' ? 'Forward to Zapier' : `Forward to Zapier - ${name}`;
    trackClick('Forward Link', uiPosition, {
      state_event_was_triggered_on: 'Survey Loading Module',
    });
  };

  const responsesPageUrl = useShortUrl(siteId.toString(), window.location.href);
  const handleCopySuccess = () => trackItemShared({ item: 'Survey Responses Page' });

  return (
    <Container>
      <div>
        <EmptyState
          title={t('activation:empty-states.pending-data.surveys.title')}
          subtitle={t('activation:empty-states.pending-data.surveys.subtitle')}
          img={SPINNER_ICON}
          kind="condensed"
          buttonKind="secondary"
        />
      </div>
      <div>
        <ShareBox>
          <ForwardResponsesTitle textType="subtitle2">
            {t('common:survey.creation.forwarding.sharing-results')}
          </ForwardResponsesTitle>
          {responsesPageUrl.status === 'success' ? (
            <LinkBox>
              <ShareUrlContainer>
                <ShareUrl>{responsesPageUrl.data}</ShareUrl>
                <CopyButton
                  onSuccess={handleCopySuccess}
                  text={responsesPageUrl.data}
                  title="Copy link to clipboard"
                >
                  <Icon icon="copy" color={tokens.color.neutral_700} size="16" />
                </CopyButton>
              </ShareUrlContainer>
            </LinkBox>
          ) : null}
          <ForwardResponsesTitle textType="subtitle2">
            {t('common:survey.creation.forwarding.need-to-forward-responses')}
          </ForwardResponsesTitle>
          <LinkBox>
            <ForwardPanel
              widgetType="poll"
              widgetId={surveyId}
              siteId={siteId}
              onEdit={onEdit}
              hasEmailForwarding
              emailEnabled={emailEnabled}
              hasSlackForwarding
              hasMsTeamsForwarding
              hasWebhooksForwarding={webhooksEnabled}
            />
          </LinkBox>
          <ForwardResponsesTitle textType="subtitle2">
            {t('common:survey.creation.forwarding.zapier.title')}
          </ForwardResponsesTitle>
          <LinkBox>
            {zapierTemplates.map(({ name, icon, surveyUrl, copy }, index) => (
              <ZapierTemplate
                key={surveyUrl}
                icon={icon as IconName}
                url={surveyUrl}
                copy={t(copy)}
                isLastTemplate={index === zapierTemplates.length - 1}
                withoutTree
                onClick={() => trackZapier(name)}
              />
            ))}
          </LinkBox>
        </ShareBox>
      </div>
    </Container>
  );
}
